import { Box, Card, CardContent, Grid, Skeleton, Typography } from '@mui/material';
import { useDashboard } from '../DashboardContext';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ShoppingCart as SalesIcon,
  AccountBalanceWallet as DebtIcon,
  HourglassBottom as PendingIcon,
  Inventory2 as InventoryIcon,
  People as ClientsIcon,
  Receipt as AvgIcon,
  Payments as PaymentsIcon,
  LocalShipping as SupplyIcon,
  Warning as RiskIcon,
  AccountBalance as PayablesIcon,
  EventBusy as OverdueIcon,
  Savings as WalletIcon,
  SwapVert as WalletMoveIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAppSocket } from '../../../providers/AppSocketContext';
import { DashboardMetricLabel } from './DashboardInfoTip';
import { canRead, canViewOperationField } from '../../../util/access';
import { useAuth } from '../../../hooks/useAuth';

const formatNumber = (num: number, currency: string) => {
  return new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num) + ` ${currency}`;
};

const formatCount = (num: number) => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

interface KPICardProps {
  title: string;
  helpKey: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
  loading?: boolean;
  requires?: 'itemCost' | 'supplyCost';
}

const KPICard = ({ title, helpKey, value, subtitle, icon, color, loading }: KPICardProps) => {
  if (loading) {
    return (
      <Card sx={{ height: '100%', borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: 2.5 }}>
          <Skeleton variant="circular" width={44} height={44} />
          <Skeleton variant="text" width="60%" sx={{ mt: 1.5 }} />
          <Skeleton variant="text" width="80%" height={36} />
          <Skeleton variant="text" width="40%" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: 3,
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
        },
        borderTop: `3px solid ${color}`,
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <DashboardMetricLabel label={title} helpKey={helpKey} />
            <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5, color: 'text.primary', lineHeight: 1.2 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              bgcolor: `${color}15`,
              borderRadius: 2,
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box sx={{ color, fontSize: 28 }}>{icon}</Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const KPICards = () => {
  const { kpi, sectionLoading, currency } = useDashboard();
  const { access } = useAuth();
  const { t } = useTranslation();
  const { appSettings } = useAppSocket();
  const themeColor = appSettings?.UI_THEME_PRIMARY?.value || '#8b1010';

  const cards: Array<KPICardProps & { section: keyof typeof sectionLoading }> = [
    {
      section: 'sales',
      title: t('common:dashboard.kpi.totalSales'),
      helpKey: 'kpi.totalSales',
      value: formatNumber(kpi.totalSales, currency),
      subtitle: `${formatCount(kpi.salesCount)} ${t('common:dashboard.kpi.transactions')}`,
      icon: <SalesIcon fontSize="inherit" />,
      color: '#2e7d32',
    },
    {
      section: 'sales',
      requires: 'itemCost',
      title: t('common:dashboard.kpi.totalProfit'),
      helpKey: 'kpi.totalProfit',
      value: formatNumber(kpi.totalProfit, currency),
      subtitle: t('common:dashboard.kpi.afterCost'),
      icon: <TrendingUpIcon fontSize="inherit" />,
      color: kpi.totalProfit >= 0 ? '#2e7d32' : '#d32f2f',
    },
    {
      section: 'inventory',
      requires: 'itemCost',
      title: t('common:dashboard.kpi.projectedProfit'),
      helpKey: 'kpi.projectedProfit',
      value: formatNumber(kpi.projectedProfit, currency),
      subtitle: t('common:dashboard.kpi.ifAllSoldNow'),
      icon: <TrendingUpIcon fontSize="inherit" />,
      color: kpi.projectedProfit >= 0 ? '#0288d1' : '#ed6c02',
    },
    {
      section: 'debt',
      title: t('common:dashboard.kpi.outstandingDebt'),
      helpKey: 'kpi.outstandingDebt',
      value: formatNumber(kpi.totalDebt, currency),
      subtitle: `${formatCount(kpi.clientCount)} ${t('common:dashboard.kpi.clients')} · ${formatNumber(kpi.totalAgentDebt, currency)} agent`,
      icon: <DebtIcon fontSize="inherit" />,
      color: '#d32f2f',
    },
    {
      section: 'payments',
      title: t('common:dashboard.kpi.pendingDeposits'),
      helpKey: 'kpi.pendingDeposits',
      value: formatNumber(kpi.pendingDeposits, currency),
      subtitle: t('common:dashboard.kpi.awaitingReconciliation'),
      icon: <PendingIcon fontSize="inherit" />,
      color: '#ed6c02',
    },
    {
      section: 'inventory',
      requires: 'itemCost',
      title: t('common:dashboard.kpi.inventoryValue'),
      helpKey: 'kpi.inventoryValue',
      value: formatNumber(kpi.totalInventoryValue, currency),
      subtitle: t('common:dashboard.kpi.acrossAllDepots'),
      icon: <InventoryIcon fontSize="inherit" />,
      color: '#0288d1',
    },
    {
      section: 'sales',
      title: t('common:dashboard.kpi.avgOrderValue'),
      helpKey: 'kpi.avgOrderValue',
      value: formatNumber(kpi.avgOrderValue, currency),
      subtitle: t('common:dashboard.kpi.perTransaction'),
      icon: <AvgIcon fontSize="inherit" />,
      color: '#7b1fa2',
    },
    {
      section: 'payments',
      title: t('common:dashboard.kpi.totalPayments'),
      helpKey: 'kpi.totalPayments',
      value: formatNumber(kpi.totalPayments, currency),
      subtitle: `${formatCount(kpi.paymentCount)} ${t('common:dashboard.kpi.transactions')}`,
      icon: <PaymentsIcon fontSize="inherit" />,
      color: '#00695c',
    },
    {
      section: 'activity',
      requires: 'supplyCost',
      title: t('common:dashboard.kpi.totalSupplies'),
      helpKey: 'kpi.totalSupplies',
      value: formatNumber(kpi.totalSupplyValue, currency),
      subtitle: t('common:dashboard.kpi.purchased'),
      icon: <SupplyIcon fontSize="inherit" />,
      color: '#455a64',
    },
    {
      section: 'activity',
      title: t('common:dashboard.kpi.supplierPayments'),
      helpKey: 'kpi.supplierPayments',
      value: formatNumber(kpi.totalSupplierPayments, currency),
      subtitle: t('common:dashboard.kpi.supplierPaymentsHint'),
      icon: <PaymentsIcon fontSize="inherit" />,
      color: '#00695c',
    },
    {
      section: 'activity',
      requires: 'supplyCost',
      title: t('common:dashboard.kpi.supplierBalance'),
      helpKey: 'kpi.supplierBalance',
      value: formatNumber(kpi.supplierBalanceOwed, currency),
      subtitle: t('common:dashboard.kpi.supplierBalanceHint'),
      icon: <PayablesIcon fontSize="inherit" />,
      color: '#5d4037',
    },
    {
      section: 'activity',
      requires: 'supplyCost',
      title: t('common:dashboard.kpi.overdueSupplies'),
      helpKey: 'kpi.overdueSupplies',
      value: kpi.overdueSupplyCount.toString(),
      subtitle: t('common:dashboard.kpi.overdueSuppliesHint'),
      icon: <OverdueIcon fontSize="inherit" />,
      color: kpi.overdueSupplyCount > 0 ? '#d32f2f' : '#2e7d32',
    },
    {
      section: 'debt',
      title: t('common:dashboard.kpi.creditRisk'),
      helpKey: 'kpi.creditRisk',
      value: kpi.creditRiskCount.toString(),
      subtitle: t('common:dashboard.kpi.clientsAtRisk'),
      icon: <RiskIcon fontSize="inherit" />,
      color: kpi.creditRiskCount > 0 ? '#d32f2f' : '#2e7d32',
    },
    {
      section: 'wallet',
      title: t('common:dashboard.kpi.walletBalance'),
      helpKey: 'kpi.walletBalance',
      value: formatNumber(kpi.walletBalance, currency),
      subtitle: t('common:dashboard.kpi.walletBalanceHint', {
        active: formatCount(kpi.walletActiveCount),
        total: formatCount(kpi.walletCount),
      }),
      icon: <WalletIcon fontSize="inherit" />,
      color: '#1565c0',
    },
    {
      section: 'wallet',
      title: t('common:dashboard.kpi.walletMovements'),
      helpKey: 'kpi.walletMovements',
      value: formatNumber(kpi.walletCredits - kpi.walletDebits, currency),
      subtitle: t('common:dashboard.kpi.walletMovementsHint', {
        movements: formatCount(kpi.walletMovementCount),
        credits: formatNumber(kpi.walletCredits, currency),
        debits: formatNumber(kpi.walletDebits, currency),
      }),
      icon: <WalletMoveIcon fontSize="inherit" />,
      color: '#00695c',
    },
  ];

  return (
    <Grid container spacing={2}>
      {cards.filter((card) =>
        card.requires === 'itemCost'
          ? canRead(access, 'item', 'cost_price')
          : card.requires === 'supplyCost'
            ? canViewOperationField(access, 'supply', 'cost')
            : true
      ).map((card, idx) => {
        const { section, ...cardProps } = card;
        return (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={idx}>
            <KPICard {...cardProps} loading={sectionLoading[section]} />
          </Grid>
        );
      })}
    </Grid>
  );
};

export default KPICards;
